import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { errorCatch } from './errorCatcher.reducer';

class ErrorParser {
  businessId: [string] | null;
  legalBusinessName: [string] | null;
  country: [string] | null;
  region: [string] | null;
  city: [string] | null;
  postalCode: [string] | null;

  constructor(
    taxId?: [string],
    name?: [string],
    country?: [string],
    state?: [string],
    city?: [string],
    postalCode?: [string]
  ) {
    this.businessId = taxId || null;
    this.legalBusinessName = name || null;
    this.country = country || null;
    this.region = state || null;
    this.city = city || null;
    this.postalCode = postalCode || null;
  }
}

export type InitState = {
  companyDataValidation: {
    status: string;
    data: companyDataValidationType;
  };
  emailValidation: {
    status: string;
    data: emailValidationType;
  };
};

export const initialState = {
  companyDataValidation: {
    status: '',
    data: {
      businessId: null,
      legalBusinessName: null,
      country: null,
      region: null,
      city: null,
      postalCode: null
    }
  },
  emailValidation: {
    status: '',
    data: {
      email: null
    }
  }
} as InitState;

export type companyDataValidationType = {
  businessId: string | null;
  legalBusinessName: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  postalCode: string | null;
};

type emailValidationType = {
  email: string | null;
};

type companyDataValidationRequest = {
  taxId: string;
  name: string;
  country: string;
  countryIso2: string;
  state: string;
  stateId: string;
  city: string;
  postalCode: string;
};

export const companyDataValidation = createAsyncThunk(
  'validationData/companyData',
  async (validationFields: companyDataValidationRequest, { dispatch }) => {
    try {
      const { data } = await Axios.post('/api/v1/billing/company/validate', validationFields);
      if (data && data.errors) {
        const { taxId, name, country, state, city, postalCode } = data.errors;
        return new ErrorParser(taxId, name, country, state, city, postalCode);
      }
      return data;
    } catch (error) {
      dispatch(errorCatch({ place: 'validationData/companyData.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

export const emailValidation = createAsyncThunk(
  'validationData/email',
  async (validationFields: emailValidationType, { dispatch }) => {
    try {
      const { data } = await Axios.post('/api/v1/billing/company/users/validate', validationFields);
      if (data && data.errors) {
        return data.errors;
      }
      return data;
    } catch (error) {
      dispatch(errorCatch({ place: 'validationData/email.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

const slice = createSlice({
  name: 'validationData',
  initialState,
  reducers: {
    resetCompanyDataValidationState: state => {
      state.companyDataValidation = initialState.companyDataValidation;
    },
    resetEmailValidationState: state => {
      state.emailValidation = initialState.emailValidation;
    }
  },
  extraReducers: {
    //company data
    [companyDataValidation.pending.toString()]: state => {
      state.companyDataValidation.data = initialState.companyDataValidation.data;
      state.companyDataValidation.status = 'loading';
    },
    [companyDataValidation.fulfilled.toString()]: (state, action) => {
      state.companyDataValidation.data = action.payload;
      state.companyDataValidation.status = 'success';
    },
    [companyDataValidation.rejected.toString()]: state => {
      state.companyDataValidation.status = 'rejected';
    },
    //email
    [emailValidation.pending.toString()]: state => {
      state.emailValidation.data = initialState.emailValidation.data;
      state.emailValidation.status = 'loading';
    },
    [emailValidation.fulfilled.toString()]: (state, action) => {
      state.emailValidation.data = action.payload;
      state.emailValidation.status = 'success';
    },
    [emailValidation.rejected.toString()]: state => {
      state.emailValidation.data.email = null;
      state.emailValidation.status = 'rejected';
    }
  }
});

export const { resetCompanyDataValidationState, resetEmailValidationState } = slice.actions;
export default slice.reducer;
